/**
 * Pure logic for the Type signature desk (SN-19542). Kept canvas-free so it
 * is unit-testable under jsdom, which has no canvas implementation.
 */

/** Measures rendered text width for a CSS font shorthand (e.g. from a 2d context). */
export type TextMeasurer = (text: string, font: string) => number;

export const MAX_SIGNATURE_FONT_SIZE = 110;
export const MIN_SIGNATURE_FONT_SIZE = 24;
const FIT_STEP = 4;

export type SignatureFontStyle = {
  label: string;
  /**
   * Quoted CSS family plus fallback. This exact string is used BOTH in
   * `ctx.font` and in `document.fonts.load()` — the two must agree or the
   * load resolves without ever fetching the face and the export silently
   * bakes the fallback font into the PNG.
   */
  family: string;
  /** The weight the vendored face ships in; part of the font shorthand. */
  weight: number;
};

export const SIGNATURE_FONT_STYLES: readonly SignatureFontStyle[] = [
  { label: "Style 1", family: '"Dancing Script", cursive', weight: 500 },
  { label: "Style 2", family: '"Great Vibes", cursive', weight: 400 },
  { label: "Style 3", family: '"Caveat", cursive', weight: 500 },
  { label: "Style 4", family: '"Homemade Apple", cursive', weight: 400 }
];

/** CSS font shorthand shared by ctx.font and document.fonts.load. */
export function signatureFontShorthand(
  style: SignatureFontStyle,
  sizePx: number
): string {
  return `${style.weight} ${sizePx}px ${style.family}`;
}

/**
 * Largest font size (stepping down from the maximum) at which `text` fits
 * within `maxWidth`, clamped to the minimum so a very long name shrinks
 * rather than clips (AC8).
 */
export function fitFontSize(
  measure: TextMeasurer,
  text: string,
  family: string,
  maxWidth: number
): number {
  let size = MAX_SIGNATURE_FONT_SIZE;
  while (size > MIN_SIGNATURE_FONT_SIZE) {
    if (measure(text, `${size}px ${family}`) <= maxWidth) {
      return size;
    }
    size -= FIT_STEP;
  }
  return MIN_SIGNATURE_FONT_SIZE;
}

const TRIM_PADDING_PX = 8;

/**
 * Crop transparent margins so the exported PNG matches the shape
 * `getTrimmedCanvas()` produces on the draw desk. Canvas-touching, so it is
 * exercised by Playwright rather than jsdom unit tests.
 */
export function trimCanvasAlpha(source: HTMLCanvasElement): HTMLCanvasElement {
  const context = source.getContext("2d");
  if (!context) return source;

  const { data } = context.getImageData(0, 0, source.width, source.height);
  let minX = source.width;
  let minY = source.height;
  let maxX = 0;
  let maxY = 0;
  for (let y = 0; y < source.height; y++) {
    for (let x = 0; x < source.width; x++) {
      if (data[(y * source.width + x) * 4 + 3] > 0) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < minX) return source;

  const trimmed = document.createElement("canvas");
  trimmed.width = maxX - minX + TRIM_PADDING_PX * 2;
  trimmed.height = maxY - minY + TRIM_PADDING_PX * 2;
  trimmed
    .getContext("2d")
    ?.drawImage(
      source,
      minX - TRIM_PADDING_PX,
      minY - TRIM_PADDING_PX,
      trimmed.width,
      trimmed.height,
      0,
      0,
      trimmed.width,
      trimmed.height
    );
  return trimmed;
}
